// Public giveaway (raffle) landing page — bee/honey themed.
// Stack: CRA + React Router + framer-motion + i18next (matches the app).
//
// Sections: Hero -> Prizes -> Participants -> Giveaway machine -> Transparency.
// Winner is selected client-side at random for the prize the user picked.

import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import i18n from '../i18n';
import { raffleTranslations, RAFFLE_NS } from '../components/raffle/translations';
import { PRIZES, getPrizeById, WINNER_IMAGE } from '../components/raffle/prizes';
import { fetchRaffleParticipants } from '../services/raffleApi';
import { toStartOfDay, toEndOfDay } from '../components/raffle/utils';

import Header from '../components/raffle/Header';
import Logo from '../components/raffle/Logo';
import HoneycombBackground from '../components/raffle/HoneycombBackground';
import ScrollBees from '../components/raffle/ScrollBees';
import DateRangePicker from '../components/raffle/DateRangePicker';
import PrizeCard from '../components/raffle/PrizeCard';
import WinnerModal from '../components/raffle/WinnerModal';
import Confetti from '../components/raffle/Confetti';
import { MagneticButton } from '../components/raffle/interactions';
import {
  ArrowRightIcon,
  ArrowDownIcon,
  ShieldCheckIcon,
  MailIcon,
  PhoneIcon,
  LocationIcon,
  InstagramIcon,
  FacebookIcon,
  YoutubeIcon,
  TiktokIcon,
  TelegramIcon,
  ViberIcon,
  WhatsappIcon,
} from '../components/raffle/icons';

import '../css/raffle.css';

// Lazy-load the heaviest, below-the-fold component.
const GiveawayMachine = lazy(() => import('../components/raffle/GiveawayMachine'));

// Register the raffle translations as a dedicated namespace (once).
let i18nRegistered = false;
const registerRaffleI18n = () => {
  if (i18nRegistered) return;
  Object.entries(raffleTranslations).forEach(([lng, bundle]) => {
    i18n.addResourceBundle(lng, RAFFLE_NS, bundle, true, true);
  });
  i18nRegistered = true;
};
registerRaffleI18n();

const THEME_KEY = 'rf-theme';
const RANGE_KEY = 'rf-raffle-range';
const HERO_HONEYCOMB = `${process.env.PUBLIC_URL || ''}/raffle/hero-honeycomb.webp`;

// Company contact details shown in the footer plaque.
const CONTACT = {
  address: 'Str. Bogdan Voievod 1',
  email: 'support@tesalut.md',
  phone: '+373 60 203 777',
  phoneHref: 'tel:+37360203777',
  telegram: 'https://t.me/tesalut',
  viber: 'viber://chat?number=%2B37360203777',
  whatsapp: 'https://wa.me/37360203777',
  instagram: 'https://instagram.com/tesalut',
  facebook: 'https://facebook.com/tesalut',
  youtube: 'https://youtube.com/@tesalut',
  tiktok: 'https://tiktok.com/@tesalut',
};
const PARTICIPANTS_REFRESH_MS = 60000; // keep the list live (each refresh re-reads up to 500 orders)
const getInitialTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (e) {
    /* ignore storage errors */
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};
const getInitialRange = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(RANGE_KEY) || 'null');
    return { start: saved?.start || '', end: saved?.end || '' };
  } catch (e) {
    return { start: '', end: '' };
  }
};

const sectionMotion = (reduced) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 40 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] },
      };

const Raffle = () => {
  const { t } = useTranslation(RAFFLE_NS);
  const prefersReduced = useReducedMotion();
  const reducedMotion = !!prefersReduced;

  const [rangeStart, setRangeStart] = useState(() => getInitialRange().start); // 'YYYY-MM-DD' or ''
  const [rangeEnd, setRangeEnd] = useState(() => getInitialRange().end);

  const [participants, setParticipants] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'invalid' | 'loading' | 'ready'
  const [selectedPrizeId, setSelectedPrizeId] = useState(PRIZES[0].id);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'spinning' | 'revealed'
  const [winner, setWinner] = useState(null);
  const [drawId, setDrawId] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [confettiActive, setConfettiActive] = useState(false);
  const [theme, setTheme] = useState(getInitialTheme);
  const [switchingTheme, setSwitchingTheme] = useState(false);

  const participantsReqId = useRef(0);
  const switchTimer = useRef(null);
  const heroRef = useRef(null);

  // Pointer parallax for the hero: write normalised offsets to CSS variables so
  // the honeycomb layers and particles drift without triggering React renders.
  const handleHeroPointer = useCallback(
    (event) => {
      if (reducedMotion) return;
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty('--rf-px', px.toFixed(3));
      el.style.setProperty('--rf-py', py.toFixed(3));
    },
    [reducedMotion]
  );

  const resetHeroPointer = useCallback(() => {
    const el = heroRef.current;
    if (!el) return;
    el.style.setProperty('--rf-px', '0');
    el.style.setProperty('--rf-py', '0');
  }, []);

  const toggleTheme = useCallback(() => {
    // Suppress transitions for the swap so colours don't animate/flash.
    setSwitchingTheme(true);
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch (e) {
        /* ignore storage errors */
      }
      return next;
    });
    clearTimeout(switchTimer.current);
    switchTimer.current = setTimeout(() => setSwitchingTheme(false), 90);
  }, []);

  useEffect(() => () => clearTimeout(switchTimer.current), []);

  // Persist the range so a saved selection survives a page reload (the
  // initial state above already restores it on mount).
  useEffect(() => {
    try {
      localStorage.setItem(RANGE_KEY, JSON.stringify({ start: rangeStart, end: rangeEnd }));
    } catch (e) {
      /* ignore storage errors */
    }
  }, [rangeStart, rangeEnd]);

  // Load every customer with a completed order inside [start, end].
  // `silent` skips the loading state so background refreshes don't flash the skeleton.
  const loadParticipants = useCallback(async (start, end, { silent = false } = {}) => {
    const startDate = toStartOfDay(start);
    const endDate = toEndOfDay(end);
    if (!startDate || !endDate) {
      setParticipants([]);
      setStatus('idle');
      return;
    }
    if (startDate > endDate) {
      setParticipants([]);
      setStatus('invalid');
      return;
    }
    const reqId = participantsReqId.current + 1;
    participantsReqId.current = reqId;
    if (!silent) setStatus('loading');
    const { participants: list } = await fetchRaffleParticipants(startDate, endDate);
    if (reqId !== participantsReqId.current) return; // a newer request superseded this one
    setParticipants(list);
    setStatus('ready');
  }, []);

  useEffect(() => {
    loadParticipants(rangeStart, rangeEnd);
  }, [rangeStart, rangeEnd, loadParticipants]);

  // Poll the participant pool so the list stays live without a manual reload.
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return undefined;
    const id = setInterval(
      () => loadParticipants(rangeStart, rangeEnd, { silent: true }),
      PARTICIPANTS_REFRESH_MS
    );
    return () => clearInterval(id);
  }, [rangeStart, rangeEnd, loadParticipants]);

  // Reset any in-progress draw whenever the range changes, since the previous
  // winner belonged to a different participant pool.
  const resetDraw = useCallback(() => {
    setPhase('idle');
    setWinner(null);
    setModalOpen(false);
    setConfettiActive(false);
  }, []);

  const handleChangeRange = useCallback(
    (start, end) => {
      setRangeStart(start);
      setRangeEnd(end);
      resetDraw();
    },
    [resetDraw]
  );

  // Keep <html lang> + document title in sync for a11y / SEO.
  useEffect(() => {
    const apply = () => {
      document.documentElement.lang = i18n.language || 'ru';
      document.title = `${t('header.brand')} · ${t('header.tagline')}`;
    };
    apply();
    i18n.on('languageChanged', apply);
    return () => i18n.off('languageChanged', apply);
  }, [t]);

  // Auto-clear confetti so it does not linger.
  useEffect(() => {
    if (!confettiActive) return undefined;
    const id = setTimeout(() => setConfettiActive(false), 6500);
    return () => clearTimeout(id);
  }, [confettiActive]);

  const selectedPrize = getPrizeById(selectedPrizeId);
  const selectedPrizeTitle = t(selectedPrize.titleKey);

  const scrollTo = useCallback(
    (id) => {
      document.getElementById(id)?.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    },
    [reducedMotion]
  );

  const startDraw = useCallback(() => {
    if (!participants.length || phase === 'spinning') return;
    // Winner selection (exactly as specified).
    const picked = participants[Math.floor(Math.random() * participants.length)];
    setModalOpen(false);
    setConfettiActive(false);
    setWinner(picked);
    setDrawId((id) => id + 1);
    setPhase('spinning');
  }, [participants, phase]);

  const handleSpinComplete = useCallback(() => {
    setPhase('revealed');
    setConfettiActive(true);
    setModalOpen(true);
  }, []);

  const handleSelectFromCard = useCallback(
    (id) => {
      if (phase === 'spinning') return;
      setSelectedPrizeId(id);
      scrollTo('rf-machine');
    },
    [phase, scrollTo]
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setConfettiActive(false);
  }, []);

  const handleAgain = useCallback(() => {
    setModalOpen(false);
    setConfettiActive(false);
    startDraw();
  }, [startDraw]);

  const participantCount = participants.length;

  const heroStats = useMemo(
    () => [
      { id: 'participants', value: participantCount, label: t('hero.statParticipants') },
      { id: 'prizes', value: PRIZES.length, label: t('hero.statPrizes') },
    ],
    [participantCount, t]
  );

  return (
    <div
      className={`rf-page${switchingTheme ? ' rf-page--switching' : ''}`}
      id="rf-top"
      data-theme={theme}
    >
      <a className="rf-skip" href="#rf-main">
        {t('header.skipToContent')}
      </a>

      <Header theme={theme} onToggleTheme={toggleTheme} />

      <main id="rf-main" className="rf-main">
        {/* SECTION 1 — HERO */}
        <section
          className="rf-section rf-hero rf-grain"
          aria-labelledby="rf-hero-title"
          ref={heroRef}
          onMouseMove={handleHeroPointer}
          onMouseLeave={resetHeroPointer}
        >
          <HoneycombBackground particles={reducedMotion ? 0 : 7} withParticles={!reducedMotion} />
          <div className="rf-hero__inner">
            <motion.div
              className="rf-hero__content"
              initial={reducedMotion ? false : { opacity: 0, y: 30 }}
              animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <h1 id="rf-hero-title" className="rf-hero__title">
                {t('hero.titlePrefix')}
                <span className="rf-grad-text">{t('hero.titleAccent')}</span>
                {t('hero.titleSuffix')}
              </h1>

              <p className="rf-hero__subtitle">{t('hero.subtitle')}</p>

              <div className="rf-hero__actions">
                <MagneticButton
                  type="button"
                  className="rf-btn rf-btn--primary rf-btn--lg rf-btn--gold"
                  reducedMotion={reducedMotion}
                  strength={0.15}
                  onClick={() => scrollTo('rf-machine')}
                  whileTap={reducedMotion ? undefined : { scale: 0.97 }}
                >
                  {t('hero.cta')}
                  <ArrowDownIcon size={18} />
                </MagneticButton>
                <button
                  type="button"
                  className="rf-btn rf-btn--ghost rf-btn--lg"
                  onClick={() => scrollTo('rf-prizes')}
                >
                  {t('hero.secondaryCta')}
                  <ArrowRightIcon size={18} />
                </button>
              </div>

              <dl className="rf-hero__stats">
                {heroStats.map((stat) => (
                  <div key={stat.id} className="rf-hero__stat">
                    <dt className="rf-hero__stat-value">{stat.value}</dt>
                    <dd className="rf-hero__stat-label">{stat.label}</dd>
                  </div>
                ))}
                <div className="rf-hero__stat rf-hero__stat--live">
                  <dt className="rf-hero__stat-value">
                    <span className="rf-hero__pulse" aria-hidden="true" />
                  </dt>
                  <dd className="rf-hero__stat-label">{t('hero.statLive')}</dd>
                </div>
              </dl>
            </motion.div>

            <motion.div
              className="rf-hero__visual"
              aria-hidden="true"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.94 }}
              animate={reducedMotion ? undefined : { opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <img
                className="rf-hero__honeycomb"
                src={HERO_HONEYCOMB}
                alt=""
                width="1500"
                height="1226"
                loading="eager"
                decoding="async"
              />
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — PRIZES */}
        <motion.section
          id="rf-prizes"
          className="rf-section rf-prizes"
          aria-labelledby="rf-prizes-title"
          {...sectionMotion(reducedMotion)}
        >
          <header className="rf-section__head">
            <h2 id="rf-prizes-title" className="rf-section__title">
              {t('prizes.title')}
            </h2>
            <p className="rf-section__subtitle">{t('prizes.subtitle')}</p>
          </header>

          <div className="rf-prizes__grid">
            {PRIZES.map((prize, index) => (
              <PrizeCard
                key={prize.id}
                prize={prize}
                index={index}
                active={selectedPrizeId === prize.id}
                reducedMotion={reducedMotion}
                onSelect={handleSelectFromCard}
              />
            ))}
          </div>
        </motion.section>

        {/* SECTION 3 — PARTICIPANTS */}
        <motion.section
          id="rf-participants"
          className="rf-section rf-participants"
          aria-labelledby="rf-participants-title"
          {...sectionMotion(reducedMotion)}
        >
          <header className="rf-section__head">
            <h2 id="rf-participants-title" className="rf-section__title">
              {t('participants.title')}
            </h2>
            <p className="rf-section__subtitle">
              {t('participants.subtitle')}
              {status === 'ready' && participantCount > 0 && (
                <span className="rf-count-pill">
                  {participantCount} {t('participants.countLabel')}
                </span>
              )}
            </p>
          </header>

          <DateRangePicker
            startValue={rangeStart}
            endValue={rangeEnd}
            onChangeRange={handleChangeRange}
            invalid={status === 'invalid'}
          />

          {status === 'idle' && (
            <div className="rf-state">{t('participants.pickRange')}</div>
          )}

          {status === 'invalid' && (
            <div className="rf-state rf-state--error" role="alert">
              <p>{t('dateRange.invalid')}</p>
            </div>
          )}

          {status === 'ready' && participantCount === 0 && (
            <div className="rf-state">{t('participants.empty')}</div>
          )}
        </motion.section>

        {/* SECTION 4 — GIVEAWAY MACHINE */}
        <motion.section
          id="rf-machine"
          className="rf-section rf-machine-section"
          aria-labelledby="rf-machine-title"
          {...sectionMotion(reducedMotion)}
        >
          <header className="rf-section__head">
            <h2 id="rf-machine-title" className="rf-section__title">
              {t('machine.title')}
            </h2>
            <p className="rf-section__subtitle">{t('machine.subtitle')}</p>
          </header>

          <Suspense fallback={<div className="rf-machine__skeleton" aria-hidden="true" />}>
            <GiveawayMachine
              participants={participants}
              winner={winner}
              drawId={drawId}
              phase={phase}
              selectedPrizeId={selectedPrizeId}
              onStart={startDraw}
              onSelectPrize={setSelectedPrizeId}
              onSpinComplete={handleSpinComplete}
              reducedMotion={reducedMotion}
            />
          </Suspense>
        </motion.section>

        {/* SECTION 5 — TRANSPARENCY */}
        <motion.section
          id="rf-transparency"
          className="rf-section rf-transparency"
          aria-labelledby="rf-transparency-title"
          {...sectionMotion(reducedMotion)}
        >
          <div className="rf-transparency__card">
            <span className="rf-transparency__icon" aria-hidden="true">
              <ShieldCheckIcon size={28} />
            </span>
            <h2 id="rf-transparency-title" className="rf-transparency__statement">
              {t('transparency.statement')}
            </h2>
            <p className="rf-transparency__detail">{t('transparency.detail')}</p>
          </div>
        </motion.section>
      </main>

      <footer className="rf-footer rf-grain">
        <div className="rf-footer__inner">
          <div className="rf-footer__col rf-footer__col--brand">
            <a className="rf-footer__brand" href="#rf-top" aria-label={t('header.brand')}>
              <Logo className="rf-footer__logo" />
            </a>
            <p className="rf-footer__addr">
              <LocationIcon size={18} />
              <span>{CONTACT.address}</span>
            </p>
            <a className="rf-footer__privacy" href="#rf-transparency">
              {t('footer.privacy')}
            </a>
          </div>

          <div className="rf-footer__col">
            <h3 className="rf-footer__title">{t('footer.support')}</h3>
            <a className="rf-footer__link" href={`mailto:${CONTACT.email}`}>
              <MailIcon size={18} />
              <span>{CONTACT.email}</span>
            </a>
            <a className="rf-footer__link" href={CONTACT.phoneHref}>
              <PhoneIcon size={18} />
              <span>{CONTACT.phone}</span>
            </a>
            <div className="rf-footer__socials">
              <a
                className="rf-footer__social"
                href={CONTACT.telegram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
              >
                <TelegramIcon size={20} />
              </a>
              <a
                className="rf-footer__social"
                href={CONTACT.viber}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Viber"
              >
                <ViberIcon size={20} />
              </a>
              <a
                className="rf-footer__social"
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                <WhatsappIcon size={20} />
              </a>
            </div>
          </div>

          <div className="rf-footer__col">
            <h3 className="rf-footer__title">{t('footer.follow')}</h3>
            <div className="rf-footer__socials">
              <a
                className="rf-footer__social"
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <InstagramIcon size={20} />
              </a>
              <a
                className="rf-footer__social"
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FacebookIcon size={20} />
              </a>
              <a
                className="rf-footer__social"
                href={CONTACT.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <YoutubeIcon size={20} />
              </a>
              <a
                className="rf-footer__social"
                href={CONTACT.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
              >
                <TiktokIcon size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="rf-footer__bottom">
          <p>{t('footer.rights', { year: new Date().getFullYear() })}</p>
        </div>
      </footer>

      <ScrollBees reducedMotion={reducedMotion} />

      <Confetti active={confettiActive} reducedMotion={reducedMotion} />

      <WinnerModal
        open={modalOpen}
        winner={winner}
        prizeTitle={selectedPrizeTitle}
        winnerImage={WINNER_IMAGE}
        prizeImage={selectedPrize.image}
        onClose={handleCloseModal}
        onAgain={handleAgain}
        reducedMotion={reducedMotion}
      />
    </div>
  );
};

export default Raffle;
